<template>
    <v-container fluid class="pa-4">
        <v-card class="mb-4">
            <v-card-title class="text-h6">
                <v-icon left color="primary">mdi-checkbox-multiple-marked</v-icon>
                Inbox Approvazioni
                <v-spacer></v-spacer>
                <v-chip class="mr-2" small color="orange" text-color="white" v-if="totals">
                    {{ (totals.absences || 0) + (totals.expenses || 0) }} pendenti
                </v-chip>
                <v-btn icon @click="load" :loading="loading"><v-icon>mdi-refresh</v-icon></v-btn>
            </v-card-title>

            <v-alert v-if="!loading && (totals.absences + totals.expenses) === 0" type="success" dense text class="mx-4">
                Nessuna richiesta in attesa di approvazione.
            </v-alert>

            <v-alert v-if="message" type="info" dense text class="mx-4">{{ message }}</v-alert>
        </v-card>

        <v-card v-if="absences.length" class="mb-4">
            <v-card-title class="text-subtitle-1">
                <v-icon left color="orange">mdi-beach</v-icon>
                Richieste assenza ({{ approvableAbsences.length }})
            </v-card-title>
            <v-tooltip top v-if="hiddenAbsencesCount > 0">
                <template v-slot:activator="{ on }">
                    <v-chip v-on="on" x-small class="ml-4 mb-2" color="grey lighten-2">
                        <v-icon left x-small>mdi-eye-off</v-icon>
                        Nascoste {{ hiddenAbsencesCount }} righe self-blocked
                    </v-chip>
                </template>
                <span>Non puoi approvare/rifiutare le tue stesse richieste</span>
            </v-tooltip>
            <v-data-table
                :headers="absenceHeaders"
                :items="approvableAbsences"
                disable-pagination
                hide-default-footer
                dense>
                <template v-slot:item.type_name="{ item }">
                    <v-chip x-small :color="item.type_color || 'blue'" text-color="white">
                        <v-icon left x-small>{{ item.type_icon || 'mdi-beach' }}</v-icon>
                        {{ item.type_name }}
                    </v-chip>
                </template>
                <template v-slot:item.start_at="{ item }">{{ formatAbsenceRange(item.start_at, item.end_at, item.half_day) }}</template>
                <template v-slot:item.total_hours="{ item }">{{ item.type_kind === 'break_waiver' ? '-' : `${item.total_hours}h` }}</template>
                <template v-slot:item.actions="{ item }">
                    <v-btn v-if="$store.getters.canActOn('hrabsences', 'approve', item)" icon small color="success" @click="approveAbsence(item)" title="Approva"><v-icon small>mdi-check</v-icon></v-btn>
                    <v-btn v-if="$store.getters.canActOn('hrabsences', 'reject', item)" icon small color="error" @click="openRejectAbsence(item)" title="Rifiuta"><v-icon small>mdi-close</v-icon></v-btn>
                </template>
            </v-data-table>
        </v-card>

        <v-card v-if="expenses.length">
            <v-card-title class="text-subtitle-1">
                <v-icon left color="green">mdi-cash-fast</v-icon>
                Note spese ({{ approvableExpenses.length }})
            </v-card-title>
            <v-tooltip top v-if="hiddenExpensesCount > 0">
                <template v-slot:activator="{ on }">
                    <v-chip v-on="on" x-small class="ml-4 mb-2" color="grey lighten-2">
                        <v-icon left x-small>mdi-eye-off</v-icon>
                        Nascoste {{ hiddenExpensesCount }} righe self-blocked
                    </v-chip>
                </template>
                <span>Non puoi approvare/rifiutare le tue stesse richieste</span>
            </v-tooltip>
            <v-data-table
                :headers="expenseHeaders"
                :items="approvableExpenses"
                disable-pagination
                hide-default-footer
                dense>
                <template v-slot:item.date="{ item }">{{ formatDate(item.date) }}</template>
                <template v-slot:item.amount="{ item }">{{ formatCurrency(item.amount) }}</template>
                <template v-slot:item.reimbursable_amount="{ item }"><strong>{{ formatCurrency(item.reimbursable_amount || item.amount) }}</strong></template>
                <template v-slot:item.actions="{ item }">
                    <v-btn v-if="$store.getters.canActOn('hrexpenses', 'approve', item)" icon small color="success" @click="approveExpense(item)" title="Approva"><v-icon small>mdi-check</v-icon></v-btn>
                    <v-btn v-if="$store.getters.canActOn('hrexpenses', 'reject', item)" icon small color="error" @click="openRejectExpense(item)" title="Rifiuta"><v-icon small>mdi-close</v-icon></v-btn>
                </template>
            </v-data-table>
        </v-card>

        <v-dialog v-model="rejectDialog" max-width="500">
            <v-card>
                <v-card-title>Rifiuta richiesta</v-card-title>
                <v-card-text>
                    <v-textarea v-model="rejectReason" label="Motivo del rifiuto" outlined dense rows="3" auto-grow></v-textarea>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="rejectDialog = false">Annulla</v-btn>
                    <v-btn color="error" @click="confirmReject">Rifiuta</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { formatAbsenceRange } from '@/components/utilities/hrDates'

export default {
    name: 'hr-approvals-inbox',

    data() {
        return {
            absences: [],
            expenses: [],
            totals: { absences: 0, expenses: 0 },
            message: '',
            loading: false,
            rejectDialog: false,
            rejectTarget: null,
            rejectKind: null,
            rejectReason: '',
            absenceHeaders: [
                { text: 'Dipendente', value: 'employee_full_name' },
                { text: 'Tipo', value: 'type_name' },
                // Periodo con gli orari delle richieste a ore
                { text: 'Periodo', value: 'start_at' },
                { text: 'Ore', value: 'total_hours' },
                { text: 'Motivo', value: 'reason' },
                { text: '', value: 'actions', sortable: false, align: 'end' },
            ],
            expenseHeaders: [
                { text: 'Data', value: 'date' },
                { text: 'Dipendente', value: 'employee_full_name' },
                { text: 'Categoria', value: 'category' },
                { text: 'Descrizione', value: 'description' },
                { text: 'Sostenuto', value: 'amount' },
                { text: 'Rimborsabile', value: 'reimbursable_amount' },
                { text: '', value: 'actions', sortable: false, align: 'end' },
            ],
        }
    },

    computed: {
        approvableAbsences() {
            const can = this.$store.getters.canActOn
            return (this.absences || []).filter(item =>
                can('hrabsences', 'approve', item) || can('hrabsences', 'reject', item))
        },
        approvableExpenses() {
            const can = this.$store.getters.canActOn
            return (this.expenses || []).filter(item =>
                can('hrexpenses', 'approve', item) || can('hrexpenses', 'reject', item))
        },
        hiddenAbsencesCount() {
            return (this.absences || []).length - this.approvableAbsences.length
        },
        hiddenExpensesCount() {
            return (this.expenses || []).length - this.approvableExpenses.length
        },
    },

    methods: {
        load() {
            this.loading = true
            this.message = ''
            this.axios.get('hrapprovals/inbox').then(r => {
                const d = r.data.data || {}
                this.absences = d.absences || []
                this.expenses = d.expenses || []
                this.totals = d.totals || { absences: 0, expenses: 0 }
                this.message = r.data.message || ''
                this.loading = false
            }, () => { this.loading = false })
        },
        approveAbsence(item) {
            this.axios.post(`hrabsences/${item.id}/approve`).then(() => this.load())
        },
        openRejectAbsence(item) {
            this.rejectTarget = item
            this.rejectKind = 'absence'
            this.rejectReason = ''
            this.rejectDialog = true
        },
        approveExpense(item) {
            this.axios.post(`hrexpenses/${item.id}/approve`).then(() => this.load())
        },
        openRejectExpense(item) {
            this.rejectTarget = item
            this.rejectKind = 'expense'
            this.rejectReason = ''
            this.rejectDialog = true
        },
        confirmReject() {
            if (!this.rejectTarget) return
            const url = this.rejectKind === 'absence'
                ? `hrabsences/${this.rejectTarget.id}/reject`
                : `hrexpenses/${this.rejectTarget.id}/reject`
            this.axios.post(url, { rejection_reason: this.rejectReason }).then(() => {
                this.rejectDialog = false
                this.load()
            })
        },
        formatDate(d) { return d ? new Date(d).toLocaleDateString('it-IT') : '—' },
        formatAbsenceRange,
        formatCurrency(n) {
            if (n === null || n === undefined) return '—'
            return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n)
        },
    },

    created() {
        this.$store.commit('endProgress')
        this.load()
    },
}
</script>
